import { useEffect } from 'react';

export const TESORERIA_FILTROS_KEYS = {
  recibos: 'tesoreria.recibos.filtros',
  devoluciones: 'tesoreria.devoluciones.filtros',
  ordenPago: 'tesoreria.orden-pago.filtros',
  movimientosVarios: 'tesoreria.movimientos-varios.filtros',
  movimientosBancarios: 'tesoreria.movimientos-bancarios.filtros',
  movimientosCaja: 'tesoreria.movimientos-caja.filtros',
  disponibilidades: 'tesoreria.disponibilidades.filtros',
  transferencias: 'tesoreria.transferencias.filtros',
  chequesTercero: 'tesoreria.cheques-tercero.filtros',
  chequesTercerosCartera: 'tesoreria.cheques-terceros-cartera.filtros',
  chequesPropios: 'tesoreria.cheques-propios.filtros',
  chequesPropiosPendientesAcreditar: 'tesoreria.cheques-propios-pendientes-acreditar.filtros',
  movimientosCarteras: 'tesoreria.movimientos-carteras.filtros',
  bancos: 'tesoreria.bancos.filtros',
  carteras: 'tesoreria.carteras.filtros',
  mcps: 'tesoreria.mcps.filtros',
  gruposMcp: 'tesoreria.grupos-mcp.filtros',
  marcasTarjetas: 'tesoreria.marcas-tarjetas.filtros',
} as const;

export function loadTesoreriaFiltros<T extends object>(storageKey: string): Partial<T> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Partial<T>;
    }
    return {};
  } catch {
    return {};
  }
}

export function saveTesoreriaFiltros<T extends object>(storageKey: string, filtros: T): void {
  localStorage.setItem(storageKey, JSON.stringify(filtros));
}

export function clearTesoreriaFiltrosStorage(): void {
  Object.values(TESORERIA_FILTROS_KEYS).forEach((key) => {
    localStorage.removeItem(key);
  });
}

export function usePersistTesoreriaFiltros<T extends object>(
  storageKey: string,
  filtros: T,
  deps: React.DependencyList
): void {
  useEffect(() => {
    saveTesoreriaFiltros(storageKey, filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function storedString(
  stored: Partial<Record<string, unknown>>,
  key: string,
  fallback: string
): string {
  const value = stored[key];
  return typeof value === 'string' ? value : fallback;
}

export function storedNumber(
  stored: Partial<Record<string, unknown>>,
  key: string,
  fallback: number
): number {
  const value = stored[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

export function storedNullableNumber(
  stored: Partial<Record<string, unknown>>,
  key: string,
  fallback: number | null = null
): number | null {
  if (!(key in stored)) return fallback;
  const value = stored[key];
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 0 ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = parseInt(trimmed, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return fallback;
}
